import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { take, takeUntil } from 'rxjs/operators';
import { CommunicationService } from '../../../communication/communication.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import Utils from '../../../util';
import { GetNameDialogComponent } from '../get-name.dialog';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { LooseObject } from '../../../models/loose-object.model';

interface TableStructure {
  name: string;
  sums: any;
  children: any[];
}

interface TableSection {
  name: string;
  aggData: any;
  children: TableStructure[];
}

interface DataStructure {
  label: string;
  rows: [
    {
      label: string,
      values: number[],
      unit: string
    }
  ];
}

interface Section {
  label: string;
  aggregation_method: string;
  data: DataStructure[];
}

export enum AggregationMethod {
  NONE,
  SUM,
  FIRST
}

@Component({
  selector: 'app-grouped-result-table',
  templateUrl: './grouped-result-table.component.html',
  styleUrls: ['./grouped-result-table.component.scss', '../universal-styling.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class GroupedResultTableComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  displayData: DisplayData[];
  processedData: (TableStructure | TableSection)[];
  // headers shown in the table
  headersToDisplay: string[];
  // headers to sum up for the category
  headersToProcess: string[];
  expandedCategories: string[] = [];
  data: DataStructure[] | Section[];


  @Input() hideSaveButtons = false;
  @Input() groupAggregationMethod = AggregationMethod.NONE;
  @Input() excludeFromAggregation = [];
  @Input() expandAll = true;
  @Input() limitExpansions = false;
  @Input() firstColumnHeader: string = '';

  /**
   * sets the headers to the content of the array. Must match the number of values provided
   */
  @Input() customHeaders: string[] = [];
  @Input() set setCustomHeaders(customHeaders: string[]) {
   this.customHeaders = customHeaders;
   this.initialize();
  }

  @Input() appendUnit?: boolean;
  private onDestroyNotifier = new EventEmitter();

  @Input() set setData(data: DataStructure[] | Section[]) {
    this.data = data;
    this.initialize();
  }

  sections: string[] = [];
  categories: string[] = [];
  expandableCategories: string[] = [];

  expandedElement: TableStructure | null;

  // vars related to table export functionality
  @ViewChild('tableBody') tableBody;
  tableName?: string;
  tableData: any[];
  @Input() headerDirection = 'row';

  nameString = 'name';
  labelString = 'label';
  trigger?: string;

  constructor(private projectComService: ProjectCommunicationService,
              private dialog: MatDialog,
              private commService: CommunicationService,
              private toasterService: ToastrService,
              private translationService: AnessaTranslationService,
  ) {
    super();
  }

  ngOnInit() {
    this.initialize();


    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
        // initialize has translation calls in it
        this.initialize();
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  isSection(object: DataStructure | Section) {
    return 'aggregation_method' in object;
  }

  isSectionData(object: TableStructure | TableSection) {
    return 'aggData' in object;
  }

  /**
   * Gets the result headers to display and to process
   */
  private collectProperties(data) {
    this.headersToDisplay = [];
    this.headersToProcess = [];
    if (!this.customHeaders.length) {
      // get headers from the data
      for (const cat of data) {
        for (const row of cat['rows']) {
          for (const head of Object.keys(row['values'])) {
            if (!this.headersToDisplay.includes(head)) this.headersToDisplay.push(head);
            if (!this.headersToProcess.includes(head)) this.headersToProcess.push(head);
          }
        }
      }
      this.sortHeaders(this.headersToDisplay);
      this.sortHeaders(this.headersToProcess);
    } else {
      // get custom headers and related headers to process
      for (const cat of data) {
        for (const row of cat['rows']) {
          let length = Object.keys(row['values']).length;
          if (this.appendUnit) {
            length++;
          }
          for (const head of Object.keys(row['values'])) {
            if (!this.headersToProcess.includes(head)) this.headersToProcess.push(head);
          }
          if (length !== this.customHeaders.length) {
            console.error('provided custom headers don\'t match table set');
            return;
          }
        }
      }
      this.headersToDisplay = [...this.customHeaders];
    }
    this.headersToDisplay.unshift(this.nameString);
  }

  private sortHeaders(array: Array<string>) {
    if (!array[0]) {
      return;
    }
    if (!isNaN(Date.parse(array[0]))) {
      array.sort((s1, s2) => {
        if (Date.parse(s1) < Date.parse(s2)) {
          return -1;
        }
        if (Date.parse(s1) > Date.parse(s2)) {
          return 1;
        }
        return 0;
      });
    } else if (Number(array[0])) {
      array.sort((first, second) => {
        if (Number(first) < Number(second)) {
          return -1;
        } else if (Number(first) > Number(second)) {
          return 1;
        } else {
          return 0;
        }
      });
    } else if (array[0].includes('W')) {
      // sort weeks
      if (array.length > 0 && array[0].includes('W')) {
        array.sort((s1, s2) => {
          const w1 = +s1.substr(1, 2);
          const w2 = +s2.substr(1, 2);
          const y1 = +s1.substr(4, 4);
          const y2 = +s2.substr(4, 4);

          if (y1 === y2) {
            if (w1 < w2) {
              return -1;
            }
            if (w2 < w1) {
              return 1;
            }
          } else if (y1 < y2) {
            return -1;
          } else if (y2 < y2) {
            return 1;
          }
          return 0;
        });
      }
    } else {
      array.sort();
    }
  }

  initialize() {
    if (this.data.length === 0) {
      return;
    }

    this.labelString = this.translationService.get('RESULTS.DATA.LABEL');

    this.processedData = [];
    // collect headers if object has properties
    if (this.isSection(this.data[0])) {
      this.collectProperties(this.data[0]['data']);
    } else {
      this.collectProperties(this.data);
    }

    this.categories = [];
    this.expandedCategories = [];
    this.expandableCategories = [];

    let displayLength = this.headersToDisplay.length - 1;
    if (this.appendUnit) {
      displayLength--;
    }
    if (this.headersToProcess.length !== displayLength) {
      return;
    }

    // process categories and sum up all the things
    if (this.data.length > 0 && !this.isSection(this.data[0])) {
      this.addSectionToData(this.data, this.processedData);
    } else {
      for (const section of this.data) {
        const secData: TableSection = {name: section.label, aggData: {}, children: []};
        this.addSectionToData(section['data'], secData.children);

        // sum up children for the section totals
        const sums = {};
        for (const cat of secData.children) {
          for (const heading of Object.keys(cat.sums)) {
            if (section['aggregation_method'] === 'sum') {
              if (Object.keys(sums).includes(heading)) {
                sums[heading] += cat.sums[heading];
              } else {
                sums[heading] = cat.sums[heading];
              }
            } else {
              sums[heading] = ' ';
            }
          }
        }
        secData.aggData = sums;
        this.processedData.push(secData);
      }
    }
    // initially only show categories
    this.createDisplayTable();
  }

  addSectionToData(data, targetRef) {
    for (const cat of data) {
      this.categories.push(cat['name']);
      if (cat && cat['rows'].length >= 1) {
        this.expandableCategories.push(cat['name']);
      }
      if (this.expandAll && this.expandableCategories.includes(cat['name'])) {
        this.expandedCategories.push(cat['name']);
      }
      const sums = {name: cat['name']};
      const children = [];
      for (const row of cat['rows']) {
        const childData = {name: row['label']};

        for (let i = 0; i < this.headersToProcess.length; i++) {
          const header = this.headersToProcess[i];
          if (!this.headersToDisplay[i + 1]) {
            console.error('headers provided mismatch');
            return;
          }
          childData[this.headersToDisplay[i + 1]] = row['values'][header];
          if (sums
            && header in sums
            && this.groupAggregationMethod !== AggregationMethod.NONE
            && !this.excludeFromAggregation.includes(cat['name'])) {
            sums[header] += row['values'][header];
          } else {
            if (this.groupAggregationMethod !== AggregationMethod.NONE
              && !this.excludeFromAggregation.includes(cat['name'])) {
              sums[header] = row['values'][header];
            } else {
              sums[header] = ' ';
            }
          }
        }
        if (this.appendUnit && this.headersToDisplay[this.headersToProcess.length + 1]) {
          childData[this.headersToDisplay[this.headersToProcess.length + 1]] = row['unit'];
        }
        if (cat['rows'].length >= 1) {
          children.push(this.fixObj(childData));
        }
      }
      targetRef.push({name: cat['name'], sums: this.fixObj(sums), children: children});
    }
  }

  fixObj(obj: LooseObject) {
    const result: LooseObject = {};
    for (const key of Object.keys(obj)) {
      if (key !== 'name' && Utils.isNumeric(obj[key])) {
        result[key] = this.fixToPrec(obj[key]);
      } else {
        result[key] = (obj[key] || obj[key] === 0) ? obj[key] : '-';
      }
    }
    return result;
  }

  private fixToPrec(val: number) {
    return Utils.roundToPrecision(val, this.projectComService.getPrecision());
  }

  onTableClick(param) {
    // only do work if a category was clicked. ignore children clicks
    if (param.isChild) {
      return;
    }

    if (this.categories.includes(param['name'])) {
      if (this.expandedCategories.includes(param['name'])) {
        this.expandedCategories.splice(this.expandedCategories.indexOf(param['name']), 1);
      } else {
        this.expandedCategories.push(param['name']);
      }
      this.createDisplayTable();
    }
  }

  addSectionToDisplayTable(section): DisplayData[] {
    const result = [];
    for (const set of section) {
      result.push(set.sums);
      if (this.expandedCategories.includes(set['name'])) {
        for (const child of set.children) {
          if (set.name !== 'interest' && set.children.length === 1 && this.limitExpansions && set['name'] === set.children[0].name) {
            // result.splice(result.length - 1, 1);
            // child['onlyChild'] = true;
          }
          child['isChild'] = true;
          result.push(child);
        }
      } else if (set.children.length === 1 && this.limitExpansions && set['name'] === set.children[0].name) {
        for (const child of set.children) {
          if (set.name !== 'interest' && set.children.length === 1 && this.limitExpansions && set['name'] === set.children[0].name) {
            result.splice(result.length - 1, 1);
            child['onlyChild'] = true;
          }
          child['isChild'] = true;
          result.push(child);
        }
      }
    }
    return result;
  }

  createDisplayTable() {
    this.displayData = [];
    this.sections = [];
    let result: DisplayData[] = [];
    if (this.processedData.length > 0 && Object.keys(this.processedData[0]).includes('sums')) {
      result = this.addSectionToDisplayTable(this.processedData);
    } else if (this.processedData.length > 0 && Object.keys(this.processedData[0]).includes('aggData')) {
      for (const section of this.processedData) {
        const sectionRow = {name: section.name, isSection: true};
        for (const key of this.headersToDisplay) {
          if (key === 'name') {
            continue;
          }
          sectionRow[key] = section['aggData'][key];
        }
        result.push(sectionRow);
        result = result.concat(this.addSectionToDisplayTable(section.children));
      }
    }

    // Replace any values containing translation ids with the translated string
    const valueKey = this.headersToDisplay[1];
    result.forEach(row => {
      if (typeof(row[valueKey]) === 'string' && row[valueKey].startsWith('RESULTS')) {
        row[valueKey] = this.translationService.get(row[valueKey]);
      }
    });
    this.displayData = [...result];
  }

  addCategoriesToExportData(tableSection: TableStructure[]) {
    const exportData = [];
    for (const tableEntry of tableSection) {
      // Only include the sums column if there are values in it and there is more than one child.
      let sums: TableStructure;
      if (tableEntry) {
        sums = tableEntry['sums'];
      } else {
        continue;
      }
      const includeSums = Object.values(sums).some(val => Utils.isNumeric(val));
      if (includeSums && tableEntry.children && tableEntry.children.length > 1) {
        exportData.push(sums);
      }
      // Add children data
      for (const childData of tableEntry.children) {
        exportData.push(childData);
      }
    }
    return exportData;
  }

  /**
   * Create an array of data that is ready for export
   *
   * If a table entry contains children, they will be added to the exported data
   * alongside the total sums.
   */
  createExportData() {
    let exportData = [];

    if (this.processedData && this.processedData.length > 0) {
      if (this.isSectionData(this.processedData[0])) {
        for (const section of this.processedData) {
          let sums: TableSection[] = [];
          if (section) {
            sums = section['aggData'];
            sums['name'] = section.name;
          } else {
            continue;
          }
          const includeSums = Object.values(sums).some(val => Utils.isNumeric(val));
          if (includeSums && section.children && section.children.length > 1) {
            exportData.push(sums);
          }
          // Add children data
          exportData = exportData.concat(this.addCategoriesToExportData(section.children));
        }
      } else {
        exportData = this.addCategoriesToExportData(this.processedData as TableStructure[]);
      }

    }

    return exportData;
  }

  isNumeric(element) {
    return Utils.isNumeric(element);
  }

  formatDecimalPlaces(input) {
    if (input === ' ' || input === undefined || input === null) {
      return input;
    }
    if (!Utils.isNumeric(input)) {
      return input;
    }
    let displayValue: string;
    const precision = this.projectComService.getPrecision();
    if (precision && precision > -1) {
      displayValue = String(Utils.roundToPrecisionFixed(input, precision));
    } else {
      displayValue = input.toString();
    }
    // Add commas e.g. 12345.6789 -> 12,345.6789
    if (displayValue != null) {
      const numberComponents = displayValue.split('.');
      const leftSide = numberComponents[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      numberComponents.splice(0, 1, leftSide);
      displayValue = numberComponents.join('.');
    }
    return displayValue;
  }

  openSaveDialog(isSaveToReport: boolean) {
    const dialogRef = this.dialog.open(GetNameDialogComponent, {
      width: 'max-content',
      data: {name: this.tableName, instruction: this.translationService.get('RESULTS.GENERAL.INPUT_TABLE_NAME')}
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.saveClicked) {
        if (result.name !== undefined) {
          this.tableData = this.createExportData();
          this.tableName = result.name;
            const fileContent = this.generateCSVData();
            const csvFile = new Blob([fileContent], {type: 'text/csv'});
            const url = window.URL.createObjectURL(csvFile);
            const link = document.createElement('a');
            link.download = `${this.tableName}.csv`;
            link.href = url;
            link.click();
          
        }
      }
    });
  }

 
  insertFoldedRows(tableString: string) {
    const document = new DOMParser().parseFromString(tableString, 'text/html');
    const table = document.getElementsByTagName('table').item(0);
    if (!table) return;
    const headerRow = table.rows.item(0);
    const columnInexies = Array.from(headerRow.cells).map(cell => cell.innerText.trim());
    for (let r = 1; r < table.rows.length; r++) {
      const row = table.rows.item(r);
      const rowName = row.cells.item(0).innerText;
      if (!this.expandableCategories.includes(rowName)) {
        continue;
      } else {
        row.classList.add('category');
        if (this.expandedCategories.includes(rowName)) {
          continue;
        }
      }
      let dataRows: any = this.processedData.find(cat => cat.name === rowName);
      if (dataRows === undefined) {
        for (const row of this.processedData) {
          if ('children' in row) {
            dataRows = row.children.find(cat => cat.name === rowName);
            if (dataRows !== undefined) {
              break;
            }
          }
        }
      }
      dataRows = (dataRows !== undefined) ? dataRows.children : [];
      for (const dataRow of dataRows) {
        const newRow = table.insertRow(r + 1 + dataRows.indexOf(dataRow));
        newRow.classList.add('mat-row');
        const nameCell = newRow.insertCell(0);
        nameCell.classList.add('mat-cell', 'mat-table-sticky');
        nameCell.innerHTML = dataRow['name'];
        for (let c = 1; c < columnInexies.length; c++) {
          const newCell = newRow.insertCell(c);
          newCell.classList.add('mat-cell');
          const value = dataRow[columnInexies[c]];
          newCell.innerHTML = Utils.isNumeric(value) ? value.toString() : value;
        }
      }
      r += dataRows.length;
    }
    return '<table>' + table.innerHTML + '</table>';
  }

  /**
   * Generate CSV data from a list of table objects
   *
   * Ouput data is a string representing a CSV file.
   *
   * If the header direction is set to column, headers will be displayed in new
   * columns with relevant data in rows below them.
   * If the header direction is set to row, headers will be displayed in new
   * rows with relevant data in columns beside them.
   * The default behaviour is to display headers in rows.
   *
   * @returns string representation of the generated CSV data
   */
  generateCSVData(): string {

    if (!this.headersToDisplay || this.headersToDisplay.length < 1) {
      return '';
    }

    const fileLines: string[] = [];
    this.removeHTMLTags();

    if (this.headerDirection === 'column') {
      // Displays headers in new columns
      const headersToWrite = Utils.deepCopy(this.headersToDisplay);
      headersToWrite[0] = this.firstColumnHeader;
      fileLines.push(headersToWrite.join(','));

      for (const tableEntry of this.tableData) {
        const lineData: string[] = [];
        for (const header of this.headersToDisplay) {
          lineData.push(tableEntry[header]);
        }
        fileLines.push(lineData.join(','));
      }
    } else {
      // Displays headers in new rows
      // for (const header of this.headersToDisplay) {
      for (let i = 0; i < this.headersToDisplay.length; i++) {
        const lineData: string[] = [];
        const header = this.headersToDisplay[i];
        let visibleHeader = this.headersToDisplay[i];
        if (i === 0) {
          visibleHeader = this.firstColumnHeader;
        }
        lineData.push(visibleHeader);
        for (const tableEntry of this.tableData) {
          const tableEntryElement = tableEntry[header];
          if (header === 'name') {
            lineData.push(tableEntryElement.replaceAll(',', ' and'));
          } else {
            lineData.push(tableEntryElement);
          }
        }
        fileLines.push(lineData.join(','));
      }
    }

    return fileLines.join('\n');
  }

  removeHTMLTags() {
    for (let i = 0; i < this.tableData.length; i++) {
      const element = this.tableData[i];
      element.name = element.name.replace(/(<sub>)|(<\/sub>)|(<sup>)|(<\/sup>)/g, '');
      this.tableData[i] = element;
    }
  }

  shouldShowExpandIcon(column: string, element: any) {
    return column === 'name' && this.expandableCategories.includes(element['name']) && !element['isChild'] && !element['onlyChild'];
  }
}

interface DisplayData {
  name: string;
  isSection: boolean;
};